import {
    backgroundTransitionOnClick,
    Box,
    Button,
    DesktopModal,
    Divider,
    Empty,
    Flex,
    IconFont,
    Input,
    Popover,
    usePopoverHeight
} from '@byecode/ui'
import type { IndependentAction, IndependentActionType } from '@lighthouse/core'
import { ActionHandleEventType, PAGE_STACK_DISPLAY_TYPE } from '@lighthouse/core'
import type { FlowActionTypes } from '@lighthouse/shared'
import { actionTypeInfos, flattenActionTypeInfos, getIndependentActionsMap, TriggerNodeType, useAtomData } from '@lighthouse/shared'
import { nanoid, useUncontrolled } from '@lighthouse/tools'
import { clone, find } from 'rambda'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { workflowListAtom } from '@/atoms/workflow/state'
import { FlowDetail, type FlowDetailRef } from '@/containers/FlowDetail'
import { FlowDetailShape } from '@/contexts/FlowContext'
import { getWorkflowRootNode } from '@/utils'

import { ActionTypeList } from '../EventList/ActionTypeList'

interface ActionTypeSelectorProps {
    id?: string
    value?: IndependentAction
    onChange: (value: IndependentAction) => void
}

const SCxActionTypeSelectorWrapper = styled.div`
    display: flex;
    align-items: center;
`

const SCxNotMatchWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 180px;
    height: 32px;
    padding: 4px 8px;
    border-radius: 6px;
    background: var(--color-gray-200);
    color: var(--color-gray-400);
    font-size: 16px;
    cursor: pointer;
`

const SCxNotMatchTag = styled.div`
    padding: 2px 4px;
    border-radius: 4px;
    font-size: 14px;
    background: var(--color-red-200);
    color: var(--color-red-500);
`

// const SCxListItemWrapper = styled.div`
//     overflow-x: hidden;
//     overflow-y: auto;

//     cursor: pointer;
// `

// const SCxListItem = styled.div`
//     padding: 8px 16px;
//     display: flex;
//     align-items: center;
//     gap: 8px;
//     font-size: var(--font-size-normal);
//     overflow: hidden;
//     white-space: nowrap;
//     text-overflow: ellipsis;

//     &:not([data-disabled='true']) {
//         ${backgroundTransitionOnClick}
//     }
// `

// const SCxListItemLabel = styled.div`
//     flex: 1;
// `

// const SCxListTip = styled.div`
//     padding: 8px 16px;
//     font-size: var(--font-size-sm);
//     color: var(--color-gray-400);
// `

const SCxEditButton = styled(Button)`
    flex-shrink: 0;
    margin-left: 8px;
`

type ActionTypeSelectorItem = {
    value: string
    icon: string
    label: string
    children: Omit<ActionTypeSelectorItem, 'children'>[]
    divider?: boolean
    color?: string
}

// interface ListMenuProps {
//     item: ActionTypeSelectorItem
//     type: string
//     typeName?: string
//     onCreate?: () => void
//     onClick: (value: string) => void
// }

// const ListMenu: React.FC<ListMenuProps> = ({ item, type, typeName = '动作流', onCreate, onClick }) => {
//     const [open, setOpen] = useState(false)
//     return (
//         <Popover withinPortal width={240} position="left-start" opened={open} onChange={opened => setOpen(opened)}>
//             <Popover.Target>
//                 <SCxListItem>
//                     <IconFont type={item.icon} color={item.color} size={18} />
//                     <SCxListItemLabel>{item.label}</SCxListItemLabel>
//                     <IconFont type="ArrowRightSmall" color="var(--color-gray-500)" />
//                 </SCxListItem>
//             </Popover.Target>
//             <Popover.Dropdown>
//                 {item.children.length > 0 ? (
//                     <div style={{ maxHeight: 300, overflow: 'auto' }}>
//                         {item.children.map((child, childIndex) => (
//                             <SCxListItemWrapper key={child.value}>
//                                 <SCxListItem
//                                     onClick={() => {
//                                         setOpen(true)
//                                         onClick(child.value)
//                                     }}
//                                 >
//                                     <IconFont type={child.icon} color="var(--color-gray-400)" size={18} />
//                                     {child.label}
//                                 </SCxListItem>
//                             </SCxListItemWrapper>
//                         ))}
//                     </div>
//                 ) : (
//                     <Empty
//                         icon={
//                             <IconFont type={type === 'automation' ? 'FlowArrow' : 'MouseClick'} color="var(--color-gray-400)" size={18} />
//                         }
//                         description="暂无可选的流程"
//                         styles={{ root: { height: 100 } }}
//                     />
//                 )}
//                 <SCxListTip>仅支持选择开始节点是「动作触发」的流程</SCxListTip>
//                 <Divider my={8} color="var(--color-gray-200)" />
//                 <SCxListItemWrapper>
//                     <SCxListItem onClick={onCreate}>
//                         <IconFont type="Add" color="var(--color-gray-400)" size={18} />
//                         创建{typeName}
//                     </SCxListItem>
//                 </SCxListItemWrapper>
//             </Popover.Dropdown>
//         </Popover>
//     )
// }

// const disabledDs = (ds?: DataSourceAbstract, type?: WorkflowType) => {
//     if (!ds) {
//         return true
//     }
//     switch (type) {
//         case 'action': {
//             return false
//         }
//         case 'automation': {
//             return ds.type === DataSourceType.joinDataSource || ds.sync
//         }
//         default: {
//             return true
//         }
//     }
// }

export const ActionTypeSelector: React.FC<ActionTypeSelectorProps> = ({ id: propsId, value, onChange }) => {
    const [level1Open, setLevel1Open] = useState(false)
    const { type, data } = value ?? {}
    const emitChange = useCallback(
        (next: IndependentAction) => {
            const executeFilter = value?.config?.executeFilter
            if (!executeFilter) {
                onChange(next)
                return
            }
            onChange({
                ...next,
                config: {
                    ...next.config,
                    executeFilter
                }
            })
        },
        [onChange, value]
    )
    const [_value, handleChange] = useUncontrolled({ value, onChange: emitChange })

    const { height: maxHeight, ref } = usePopoverHeight({ opened: level1Open })
    const workflowList = useAtomData(workflowListAtom)

    const flowId = type === 'flow' ? data?.flow?.flowId : type === 'automation' ? data?.automation?.flowId : ''

    const assembleActionParams = useCallback(
        (value: string, type?: FlowActionTypes): IndependentAction => {
            const id = propsId || nanoid()
            const buttonActionsMap = getIndependentActionsMap(id)
            if (type && !buttonActionsMap.has(value as IndependentActionType)) {
                return {
                    id,
                    type,
                    data: {
                        [type]: {
                            flowId: value
                        }
                    }
                } as IndependentAction
            }

            const defaultParams = buttonActionsMap.get(value as IndependentActionType)

            return defaultParams
                ? clone(defaultParams)
                : {
                      id,
                      type: ActionHandleEventType.OPEN_PAGE,
                      data: {
                          openPage: {
                              openPageId: '',
                              pageOpenType: PAGE_STACK_DISPLAY_TYPE.modal
                          }
                      }
                  }
        },
        [propsId]
    )

    const [currentFlowId, setCurrentFlowId] = useState('')
    const [flowModalOpen, setFlowModalOpen] = useState(false)
    const closeModalHandler = useRef<() => void>()
    const flowDetailRef = useRef<FlowDetailRef>(null)

    const handleOpenFlowModal = useCallback(
        (id?: string) => {
            const comingFlowId = type === 'flow' ? data?.flow?.flowId : type === 'automation' ? data?.automation?.flowId : ''
            const flowId = id ?? comingFlowId ?? ''

            setCurrentFlowId(flowId)
            setFlowModalOpen(true)
        },
        [data, type]
    )

    const handleAfterCreateFlow = useCallback(
        (id: string, type: FlowActionTypes) => {
            setCurrentFlowId(id)
            closeModalHandler.current = () => handleChange(assembleActionParams(id, type))
            setFlowModalOpen(true)
        },
        [assembleActionParams, handleChange]
    )

    const handleLevel1ItemClick = useCallback(
        (value: string) => {
            handleChange(assembleActionParams(value))
            setLevel1Open(false)
        },
        [assembleActionParams, handleChange]
    )

    const currentFlow = useMemo(() => {
        if (value?.type === 'flow') {
            return find(item => item.id === value.data.flow.flowId, workflowList)
        }
        if (value?.type === 'automation') {
            return find(item => item.id === value.data.automation.flowId, workflowList)
        }
    }, [value, workflowList])

    const currentOption = useMemo(() => {
        if (!_value) {
            return undefined
        }
        if (_value.type === 'flow' || _value.type === 'automation') {
            const comingFlowId =
                _value.type === 'flow' ? _value.data?.flow?.flowId : _value.type === 'automation' ? _value.data?.automation?.flowId : ''
            const flow = workflowList.find(item => item.id === comingFlowId)
            if (!flow) {
                return undefined
            }
            return {
                type: flow.type,
                label: flow.name,
                value: flow.id,
                icon: flow.type === 'action' ? 'MouseClick' : 'FlowArrow'
            }
        }
        return flattenActionTypeInfos(actionTypeInfos).find(item => item.value === _value.type) as ActionTypeSelectorItem
    }, [_value, workflowList])

    const currentWorkFlow = useMemo(
        () => workflowList.find(w => (type === 'flow' || type === 'automation') && flowId === w.id),
        [flowId, type, workflowList]
    )

    const isNotMatchOfTrigger = useMemo(() => {
        const node = getWorkflowRootNode(currentWorkFlow)
        return node?.data.nodeType === TriggerNodeType.WEBSOCKET_TRIGGER
    }, [currentWorkFlow])

    return (
        <SCxActionTypeSelectorWrapper ref={ref}>
            <Flex>
                <Popover withinPortal width={300} opened={level1Open} onChange={setLevel1Open}>
                    <Popover.Target>
                        {isNotMatchOfTrigger ? (
                            <SCxNotMatchWrapper>
                                <SCxNotMatchTag>触发模式不匹配</SCxNotMatchTag>
                                <Flex gap={2}>
                                    <IconFont type="CloseCircle" onClick={() => handleLevel1ItemClick('none')} />
                                    <IconFont type="ArrowDownSmall" />
                                </Flex>
                            </SCxNotMatchWrapper>
                        ) : (
                            <Box style={{ width: 180 }}>
                                <Input
                                    readOnly
                                    styles={{
                                        input: { cursor: 'pointer' }
                                    }}
                                    placeholder="请选择"
                                    prefix={currentOption && <IconFont type={currentOption?.icon ?? ''} />}
                                    value={currentOption?.label}
                                    suffix={<IconFont type="ArrowDownSmall" />}
                                />
                            </Box>
                        )}
                    </Popover.Target>
                    <Popover.Dropdown style={{ maxHeight, overflowY: 'auto' }}>
                        <ActionTypeList
                            id={propsId}
                            onChange={emitChange}
                            onClose={() => setLevel1Open(false)}
                            onAfterCreateFlow={handleAfterCreateFlow}
                        />
                    </Popover.Dropdown>
                </Popover>
                {(currentFlow?.type === 'action' || currentFlow?.type === 'automation') && (
                    <SCxEditButton
                        icon={<IconFont type="PencilSimple" color="var(--color-gray-400)" />}
                        onClick={() => handleOpenFlowModal(flowId)}
                    >
                        编辑
                    </SCxEditButton>
                )}
            </Flex>
            <DesktopModal
                position="fixed"
                target={ref.current}
                open={flowModalOpen}
                withCloseIcon={false}
                styles={{
                    modal: {
                        width: '95vw',
                        height: '90vh'
                    },
                    body: {
                        padding: 0
                    }
                }}
                onClose={() => {
                    if (flowDetailRef.current) {
                        flowDetailRef.current.requestClose()
                        return
                    }

                    setFlowModalOpen(false)
                }}
            >
                <Box style={{ position: 'relative', height: '100%', width: '100%' }}>
                    <FlowDetail
                        ref={flowDetailRef}
                        key={currentFlowId}
                        id={currentFlowId}
                        shape={FlowDetailShape.MODAL}
                        onDismiss={() => setFlowModalOpen(false)}
                        onClose={() => {
                            setFlowModalOpen(false)
                            closeModalHandler.current?.()
                        }}
                    />
                </Box>
            </DesktopModal>
        </SCxActionTypeSelectorWrapper>
    )
}
